import { signal, computed } from '@preact/signals-react'
import type { ActiveOmniMate, ActiveOmniMateDeps } from './activeOmniMate.types'

export function makeActiveOmniMate(deps: ActiveOmniMateDeps): ActiveOmniMate {
  void deps

  const localOmniMateIds = signal<string[] | undefined>(undefined)

  const activeLocalOmniMateIndex = signal<number>(0)
  const autoSwitchLocalOmniMate = signal<boolean>(true) // Default to enabled
  const isProcessingAutoSwitch = signal<boolean>(false)
  // Prevent repeated auto-switch work for the same turn/target.
  // This is intentionally NOT a signal so it doesn't create reactive dependencies.
  let lastAutoSwitchSignature: string | null = null

  function normalizeIndex(index: number, ids: string[] | undefined): number {
    // Guard against NaN/Infinity or non-integers.
    if (!Number.isFinite(index)) return 0
    const i = Math.trunc(index)
    if (i < 0) return 0

    const maxIndex = ids && ids.length > 0 ? ids.length - 1 : 0
    if (i > maxIndex) return 0
    return i
  }

  const activeLocalOmniMateId = computed(() => {
    const ids = localOmniMateIds.value
    const idx = normalizeIndex(activeLocalOmniMateIndex.value, ids)
    return ids?.[idx]
  })

  function setActiveLocalOmniMateIndex(index: number): void {
    // If we already know the ids, clamp immediately.
    // Otherwise store the (sanitized) desired index and let `updateLocalOmniMateIds` clamp later.
    if (localOmniMateIds.value) {
      activeLocalOmniMateIndex.value = normalizeIndex(index, localOmniMateIds.value)
      return
    }
    activeLocalOmniMateIndex.value = Number.isFinite(index) ? Math.max(0, Math.trunc(index)) : 0
  }

  function cycleLocalOmniMate(): void {
    const ids = localOmniMateIds.value
    if (ids && ids.length >= 2) {
      const idx = normalizeIndex(activeLocalOmniMateIndex.value, ids)
      activeLocalOmniMateIndex.value = (idx + 1) % ids.length
    }
  }

  function setAutoSwitchLocalOmniMate(enabled: boolean): void {
    autoSwitchLocalOmniMate.value = enabled
  }

  function updateLocalOmniMateIds(ids: string[] | undefined): void {
    localOmniMateIds.value = ids
    const maxIndex = ids && ids.length > 0 ? ids.length - 1 : 0
    // Ensure we never end up in a "controls nobody" state due to an invalid index.
    const idx = activeLocalOmniMateIndex.value
    if (!Number.isFinite(idx) || idx < 0 || idx > maxIndex) {
      activeLocalOmniMateIndex.value = 0
    }
  }

  function maybeAutoSwitchLocalOmniMate(input: {
    isOmniMode: boolean
    enabled: boolean
    currentPlayer: string | undefined
    playerToMateIds: string[][] | undefined
  }): void {
    const { isOmniMode, enabled, currentPlayer, playerToMateIds } = input
    if (!isOmniMode) {
      lastAutoSwitchSignature = null
      return
    }
    if (!enabled) {
      lastAutoSwitchSignature = null
      return
    }
    if (!currentPlayer) return
    if (!playerToMateIds || playerToMateIds.length < 1) return

    const slotIndex = parseInt(currentPlayer)
    if (!Number.isFinite(slotIndex) || slotIndex < 0 || slotIndex >= playerToMateIds.length) return

    const localIds = localOmniMateIds.value
    if (!localIds || localIds.length < 1) return

    const matesInSlot = playerToMateIds[slotIndex] || []
    const nextMateId = matesInSlot.find((id) => localIds.includes(id))
    if (!nextMateId) return

    const nextIndex = localIds.indexOf(nextMateId)
    if (nextIndex < 0) return
    // Important: do NOT read `activeLocalOmniMateIndex.value` here.
    // This helper is called from orchestration effects and should not subscribe to
    // manual cycling changes (backtick / bumper). Autoswitch should react to
    // turn/assignment changes, not "fight" the user's manual selection.
    const signature = `${currentPlayer}|${nextIndex}`
    if (signature === lastAutoSwitchSignature) return
    lastAutoSwitchSignature = signature

    isProcessingAutoSwitch.value = true
    activeLocalOmniMateIndex.value = nextIndex
    setTimeout(() => {
      isProcessingAutoSwitch.value = false
    }, 0)
  }

  return {
    localOmniMateIds,
    activeLocalOmniMateId,
    autoSwitchLocalOmniMate,
    setActiveLocalOmniMateIndex,
    cycleLocalOmniMate,
    setAutoSwitchLocalOmniMate,
    updateLocalOmniMateIds,
    maybeAutoSwitchLocalOmniMate,
    debug: {
      activeLocalOmniMateIndex,
      isProcessingAutoSwitch,
    },
  }
}
